import { api } from "@convex/_generated/api";
/**
 * useAIPreferences Hook
 *
 * Manages AI interface preferences following Convex patterns.
 * Preferences are stored in Convex for persistence across devices.
 */
import { useMutation, useQuery } from "convex/react";
import { useCallback, useEffect, useRef } from "react";

import { logger, PortalEvents } from "~/lib/logger";

const getUserPreferencesRef = api.ai.queries.getUserPreferences;

const setUserPreferencesRef = api.ai.queries.setUserPreferences;

export type AIMode = "floating" | "sidebar";

interface AIPreferences {
  preferredMode: AIMode;
  autoOpenSidebar: boolean;
}

interface UseAIPreferencesReturn {
  /** Current preferences (reactive) */
  preferences: AIPreferences;
  /** Whether preferences are still loading */
  isLoading: boolean;
  /** Set preferred mode (floating or sidebar) */
  setPreferredMode: (mode: AIMode) => Promise<void>;
  /** Set auto-open sidebar preference */
  setAutoOpenSidebar: (autoOpen: boolean) => Promise<void>;
}

export function useAIPreferences(): UseAIPreferencesReturn {
  const preferences = useQuery(getUserPreferencesRef);
  const setPreferencesMutation = useMutation(setUserPreferencesRef);

  const isLoading = preferences === undefined;

  // Track whether we've logged the loaded event
  const hasLoggedLoadReference = useRef(false);

  // Log when preferences are loaded
  useEffect(() => {
    if (!(isLoading || hasLoggedLoadReference.current)) {
      hasLoggedLoadReference.current = true;
      logger.info(PortalEvents.AI_PREFERENCES_LOADED, {
        preferred_mode: preferences?.preferredMode ?? "floating",
        auto_open_sidebar: preferences?.autoOpenSidebar ?? false,
      });
    }
  }, [isLoading, preferences?.preferredMode, preferences?.autoOpenSidebar]);

  const setPreferredMode = useCallback(
    async (mode: AIMode) => {
      const previousMode = preferences?.preferredMode ?? "floating";
      await setPreferencesMutation({
        preferredMode: mode,
        autoOpenSidebar: preferences?.autoOpenSidebar,
      });
      logger.info(PortalEvents.AI_PREFERENCES_CHANGED, {
        change_type: "preferred_mode",
        previous_value: previousMode,
        new_value: mode,
      });
    },
    [
      setPreferencesMutation,
      preferences?.autoOpenSidebar,
      preferences?.preferredMode,
    ]
  );

  const setAutoOpenSidebar = useCallback(
    async (autoOpen: boolean) => {
      const previousValue = preferences?.autoOpenSidebar ?? false;
      await setPreferencesMutation({
        preferredMode: preferences?.preferredMode ?? "floating",
        autoOpenSidebar: autoOpen,
      });
      logger.info(PortalEvents.AI_PREFERENCES_CHANGED, {
        change_type: "auto_open_sidebar",
        previous_value: previousValue,
        new_value: autoOpen,
      });
    },
    [
      setPreferencesMutation,
      preferences?.preferredMode,
      preferences?.autoOpenSidebar,
    ]
  );

  return {
    preferences: preferences ?? {
      preferredMode: "floating",
      autoOpenSidebar: false,
    },
    isLoading,
    setPreferredMode,
    setAutoOpenSidebar,
  };
}
